import { type TypedDocumentNode, gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';

import { TrendingPlayer } from 'components/player/TrendingPlayer';

import { HeaderSection } from '../HeaderSection';
import { PlayerBlockSkeleton } from '../PlayerBlockSkeleton';
import { SlideShow } from '../SlideShow';
import type { TrendingPlayers_anyPlayer } from './__generated__/index.graphql';

const SKELETONS = Array.from({ length: 12 }, (_, index) => ({ index }));

type Props = {
  players: Nullable<
    {
      player: TrendingPlayers_anyPlayer;
      tradesCount: number;
    }[]
  >;
  onMarketClick: (slug: string) => void;
  onStatsClick: (slug: string) => void;
  track?: ComponentProps<typeof TrendingPlayer>['track'];
  loading: boolean;
};

export const TrendingPlayers = ({
  players,
  onMarketClick,
  onStatsClick,
  track,
  loading,
}: Props) => {
  if (!loading && !players?.length) {
    return null;
  }
  return (
    <Vertical gap={3}>
      <HeaderSection
        title={
          <FormattedMessage
            id="TrendingPlayers.title"
            defaultMessage="Trending Players"
          />
        }
      />
      <SlideShow>
        <Horizontal gap={1.5}>
          {!!loading &&
            SKELETONS.map(s => <PlayerBlockSkeleton key={s.index} />)}

          {!loading &&
            players?.map(({ player, tradesCount }) => (
              <TrendingPlayer
                key={player.slug}
                anyPlayer={player}
                tradesCount={tradesCount}
                onMarketClick={slug => {
                  onMarketClick(slug);
                }}
                onStatsClick={onStatsClick}
                track={track}
              />
            ))}
        </Horizontal>
      </SlideShow>
    </Vertical>
  );
};

TrendingPlayers.fragments = {
  anyPlayer: gql`
    fragment TrendingPlayers_anyPlayer on AnyPlayerInterface {
      slug
      ...TrendingPlayer_anyPlayer
    }
    ${TrendingPlayer.fragments.anyPlayer}
  ` as TypedDocumentNode<TrendingPlayers_anyPlayer>,
};
